// BOORO: the knowledge of a space. The handbook and what the company knows,
// as pages of Markdown that link to each other.
import { oc } from "@orpc/contract";
import * as z from "zod";
import { Id } from "./ids.js";

/** The kinds of knowledge a space holds. */
export const KNOWLEDGE_KINDS = ["handbuch", "firmenwissen"] as const;
export type KnowledgeKind = (typeof KNOWLEDGE_KINDS)[number];

/** Who may read a page: the whole account, an office, a group or one agent. */
export const ACCESS_SUBJECT_KINDS = ["account", "space", "group", "bot"] as const;
export const AccessSubjectSchema = z.object({ kind: z.enum(ACCESS_SUBJECT_KINDS), id: Id });
export type AccessSubject = z.infer<typeof AccessSubjectSchema>;

export const KnowledgePageSummarySchema = z.object({
  id: Id,
  /** The office of the page; null for the knowledge of the whole account. */
  spaceId: Id.nullable(),
  kind: z.enum(KNOWLEDGE_KINDS),
  title: z.string(),
  path: z.string(),
  /** The folder of the page, as "Marke/Logos"; empty at the top. */
  folder: z.string(),
  excerpt: z.string(),
  /** The titles of the pages this one links to. */
  links: z.array(z.string()),
  /** Who may read the page. */
  receivers: z.array(AccessSubjectSchema),
  updatedAt: z.string(),
});
export type KnowledgePageSummary = z.infer<typeof KnowledgePageSummarySchema>;

export const KnowledgePageSchema = z.object({
  id: Id,
  spaceId: Id.nullable(),
  kind: z.enum(KNOWLEDGE_KINDS),
  title: z.string(),
  path: z.string(),
  folder: z.string(),
  /** The text with the wiki links resolved. */
  content: z.string(),
  /** The headings of the page. */
  outline: z.array(z.object({ level: z.number().int(), text: z.string() })),
  /** The titles of the pages this one links to. */
  links: z.array(z.string()),
  /** The agent that wrote the page last; null when a person did. */
  updatedByBotId: Id.nullable(),
  /** Whether an earlier version can be brought back. */
  hasPrevious: z.boolean(),
  updatedAt: z.string(),
});
export type KnowledgePage = z.infer<typeof KnowledgePageSchema>;

export const SaveKnowledgePageInput = z.object({
  pageId: Id.optional(),
  /** For a new page: its office, or null for the whole account. */
  spaceId: Id.nullable().optional(),
  /** For a new page: its folder. */
  folder: z.string().max(400).optional(),
  title: z.string().trim().min(1).max(120),
  kind: z.enum(KNOWLEDGE_KINDS),
  content: z.string().max(200_000),
});
export type SaveKnowledgePageInput = z.infer<typeof SaveKnowledgePageInput>;

/** A folder of a place: the whole account (spaceId null) or one office. */
/** Where a file lies: in the knowledge, beside the pages, or in the assets. */
export const FILE_AREAS = ["knowledge", "assets"] as const;
export const FileAreaSchema = z.enum(FILE_AREAS);
export type FileArea = z.infer<typeof FileAreaSchema>;

export const KnowledgeFolderSchema = z.object({ spaceId: Id.nullable(), path: z.string() });
export type KnowledgeFolder = z.infer<typeof KnowledgeFolderSchema>;

export const KnowledgeFileSummarySchema = z.object({
  id: Id,
  spaceId: Id.nullable(),
  folder: z.string(),
  area: FileAreaSchema,
  name: z.string(),
  /** One sentence on what the file is and what it is for. */
  summary: z.string(),
  mimeType: z.string(),
  size: z.number().int(),
  /** The agent that put the file here; null when a person did. */
  createdByBotId: Id.nullable(),
  receivers: z.array(AccessSubjectSchema),
  updatedAt: z.string(),
});
export type KnowledgeFileSummary = z.infer<typeof KnowledgeFileSummarySchema>;

const FolderPath = z.string().max(400);
const PlaceInput = z.object({ spaceId: Id.nullable(), area: FileAreaSchema.optional() });
const AreaInput = z.object({ area: FileAreaSchema.optional() }).optional();
const FileContent = z.object({
  name: z.string().trim().min(1).max(200),
  mimeType: z.string().max(200),
  contentBase64: z.string().min(1),
});
const AccessOutput = z.object({ receivers: z.array(AccessSubjectSchema), readerIds: z.array(Id) });
const Ok = z.object({ ok: z.literal(true) });

export const booroKnowledgeContract = {
  /** The pages of the account and of every office of the person. */
  pages: oc.output(z.array(KnowledgePageSummarySchema)),
  /** The pages one agent may read; a closed page is not in the list. */
  forAgent: oc
    .input(z.object({ botId: Id }))
    .output(z.array(z.object({ id: Id, title: z.string(), path: z.string() }))),
  /** One page, with its links resolved. */
  page: oc.input(z.object({ pageId: Id })).output(KnowledgePageSchema.nullable()),
  /** Writes a page. */
  save: oc.input(SaveKnowledgePageInput).output(z.object({ id: Id, path: z.string() })),
  /** Brings back the version before the last change. */
  restorePrevious: oc.input(z.object({ pageId: Id })).output(z.object({ ok: z.literal(true) })),
  /** Removes a page. */
  remove: oc.input(z.object({ pageId: Id })).output(z.object({ ok: z.literal(true) })),
  /** Who may read the page, and the agents that makes. */
  access: oc
    .input(z.object({ pageId: Id }))
    .output(z.object({ receivers: z.array(AccessSubjectSchema), readerIds: z.array(Id) })),
  /** Replaces who may read the page; an empty list means nobody. */
  setAccess: oc
    .input(z.object({ pageId: Id, receivers: z.array(AccessSubjectSchema).max(200) }))
    .output(z.object({ ok: z.literal(true) })),
  /** The folders of the account and of every office of the person. */
  folders: oc.input(AreaInput).output(z.array(KnowledgeFolderSchema)),
  createFolder: oc.input(PlaceInput.extend({ path: FolderPath })).output(KnowledgeFolderSchema),
  /** Renames or moves a folder with everything in it. */
  renameFolder: oc
    .input(PlaceInput.extend({ from: FolderPath, to: FolderPath }))
    .output(KnowledgeFolderSchema),
  /** Removes an empty folder. */
  removeFolder: oc.input(PlaceInput.extend({ path: FolderPath })).output(Ok),
  /** Puts a page or a file into another folder of its place. */
  move: oc
    .input(z.object({ kind: z.enum(["page", "file"]), id: Id, folder: FolderPath }))
    .output(Ok),
  /** The files of the account and of every office of the person. */
  files: oc.input(AreaInput).output(z.array(KnowledgeFileSummarySchema)),
  /** The files one agent may use; a closed file is not in the list. */
  filesForAgent: oc
    .input(z.object({ botId: Id }))
    .output(z.array(z.object({ id: Id, name: z.string(), folder: z.string() }))),
  uploadFile: oc
    .input(PlaceInput.extend({ folder: FolderPath }).merge(FileContent))
    .output(KnowledgeFileSummarySchema),
  /** Puts a new version into a file; the one before is kept. */
  replaceFile: oc
    .input(z.object({ fileId: Id }).merge(FileContent))
    .output(KnowledgeFileSummarySchema),
  /** Turns a file back to its version before the last replacement. */
  restorePreviousFile: oc.input(z.object({ fileId: Id })).output(KnowledgeFileSummarySchema),
  /** Says in a sentence what a file is and what it is for. */
  describeFile: oc
    .input(z.object({ fileId: Id, summary: z.string().max(400) }))
    .output(z.object({ summary: z.string() })),
  /** One file with its bytes, and how many earlier versions it keeps. */
  file: oc.input(z.object({ fileId: Id })).output(
    z
      .object({
        file: KnowledgeFileSummarySchema,
        contentBase64: z.string(),
        versions: z.number().int(),
      })
      .nullable(),
  ),
  renameFile: oc
    .input(z.object({ fileId: Id, name: z.string().trim().min(1).max(200) }))
    .output(z.object({ name: z.string() })),
  removeFile: oc.input(z.object({ fileId: Id })).output(Ok),
  fileAccess: oc.input(z.object({ fileId: Id })).output(AccessOutput),
  setFileAccess: oc
    .input(z.object({ fileId: Id, receivers: z.array(AccessSubjectSchema).max(200) }))
    .output(Ok),
};
